import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Base64 } from 'js-base64';
import { WhitelabelConfigStore } from '../core/config/whitelabel-config.store';
import { ConfirmService, DataService } from '../services/index';
import {
  LEGACY_PAYMENT_FORM_STORAGE_KEY,
  PAYMENT_FLOW_STORAGE_KEY
} from '../payments/payment-flow.storage';
import { environment } from '../../environments/environment';

interface ConfirmJourneySummary {
  label: string;
  details: any;
  from: string;
  to: string;
  date: string;
  seats: any[];
  fareGroups: Array<{ label: string; count: number; total: number }>;
  bookingFee: number;
  total: number;
}

@Component({
  selector: 'app-confirm',
  templateUrl: './confirm.component.html',
  styleUrls: ['./confirm.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ConfirmComponent implements OnInit, OnDestroy {
  private sub: any;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private resendTimer: ReturnType<typeof setInterval> | null = null;
  private paymentSocket: WebSocket | null = null;
  private socketFallbackStarted = false;
  private pollStartedAt = 0;
  private readonly pollIntervalMs = 10000;
  private readonly pollTimeoutMs = 120000;

  confirmData: any;
  bookingdetails: any;
  returndetails: any;
  isError = false;
  errorMessage = '';
  isWaiting = true;
  isChecking = false;
  isResending = false;
  resendSeconds = 30;
  statusMessage = '';
  paymentState: 'waiting' | 'failed' = 'waiting';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private service: ConfirmService,
    private dataService: DataService,
    public readonly configStore: WhitelabelConfigStore
  ) {}

  get config() {
    return this.configStore.snapshot.config;
  }

  ngOnInit(): void {
    this.sub = this.route.params.subscribe(params => {
      try {
        this.confirmData = JSON.parse(Base64.decode(params['id']));
        this.bookingdetails = this.confirmData?.bookingdetails;
        this.returndetails = this.confirmData?.returndetails;
        this.pollStartedAt = Date.now();
        this.startResendCooldown();
        this.connectPaymentSocket();
        this.confirmMpesa();
      } catch {
        this.isWaiting = false;
        this.isError = true;
        this.errorMessage = 'We could not load this payment request. Please return to payment and try again.';
      }
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.clearPoll();
    this.clearResendTimer();
    this.closePaymentSocket();
  }

  get routeFrom(): string {
    return this.bookingdetails?.search_from || this.bookingdetails?.result?.from || '';
  }

  get routeTo(): string {
    return this.bookingdetails?.search_to || this.bookingdetails?.result?.to || '';
  }

  get journeyDateLabel(): string {
    return this.journeyDate(this.bookingdetails);
  }

  journeyDate(details: any): string {
    const raw = String(details?.result?.departure_date || details?.search_details?.traveldate || '');
    let date: Date | null = null;
    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    const localMatch = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (isoMatch) {
      date = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    } else if (localMatch) {
      date = new Date(Number(localMatch[3]), Number(localMatch[2]) - 1, Number(localMatch[1]));
    }
    return date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('en-GB', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        }).format(date)
      : raw;
  }

  formatJourneyTime(value: any): string {
    const match = String(value || '').match(/(\d{1,2}):(\d{2})/);
    return match ? `${match[1].padStart(2, '0')}:${match[2]}` : String(value || '');
  }

  get paymentPhone(): string {
    const raw = String(
      this.confirmData?.confirm_data?.phoneno ||
      this.confirmData?.confirm_data?.data?.phone_number ||
      ''
    ).replace(/\D/g, '');
    const local = raw.length > 9 ? `0${raw.slice(-9)}` : raw;
    return local.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3');
  }

  get totalPayableKSH(): number {
    return Number(this.bookingdetails?.totalfareKSH || 0) +
      Number(this.returndetails?.totalfareKSH || 0);
  }

  get bookingFeeKSH(): number {
    return this.tripBookingFee(this.bookingdetails) + this.tripBookingFee(this.returndetails);
  }

  get payableCurrency(): 'KES' | 'UGX' {
    return this.confirmData?.confirm_data?.payment_currency === 'UGX' ? 'UGX' : 'KES';
  }

  get payableCurrencyLabel(): string {
    return this.payableCurrency === 'UGX' ? 'UGX' : 'Ksh';
  }

  get payableAmount(): number {
    const responseAmount = Number(this.confirmData?.confirm_data?.payment_amount);
    if (responseAmount || responseAmount === 0) {
      return responseAmount;
    }
    return this.payableCurrency === 'UGX'
      ? Number(this.bookingdetails?.totalfareUGX || 0) + Number(this.returndetails?.totalfareUGX || 0)
      : this.totalPayableKSH;
  }

  get journeySummaries(): ConfirmJourneySummary[] {
    return [
      this.buildJourneySummary('Outbound', this.bookingdetails),
      ...(this.returndetails ? [this.buildJourneySummary('Return', this.returndetails)] : [])
    ];
  }

  get bookingSummaryPassengers(): Array<{
    fullName: string;
    phoneNumber: string;
    seatNumber: string;
    amount: number;
  }> {
    const passengers = this.confirmData?.confirm_data?.passengers || [];
    return passengers.map((passenger: any, index: number) => ({
      fullName: passenger?.full_name || `Passenger ${index + 1}`,
      phoneNumber: passenger?.phone_number ? `+${String(passenger.phone_number).replace(/^\+/, '')}` : 'Not provided',
      seatNumber: String(passenger?.seat?.id ?? 'Not assigned'),
      amount: Number(passenger?.seat?.fare || 0)
    }));
  }

  private buildJourneySummary(label: string, details: any): ConfirmJourneySummary {
    return {
      label,
      details,
      from: details?.search_from || details?.result?.from || '',
      to: details?.search_to || details?.result?.to || '',
      date: this.journeyDate(details),
      seats: details?.seatsSelected || [],
      fareGroups: this.tripFareGroups(details),
      bookingFee: this.tripBookingFee(details),
      total: this.payableCurrency === 'UGX'
        ? Number(details?.totalfareUGX || 0)
        : Number(details?.totalfareKSH || 0)
    };
  }

  private tripBookingFee(details: any): number {
    if (this.payableCurrency === 'UGX') {
      return Number(
        details?.booking_fee_ugx ?? details?.result?.booking_fee_ugx ??
        details?.result?.service_fee_ugx ?? 0
      ) || 0;
    }
    return Number(
      details?.booking_fee ?? details?.result?.booking_fee ?? details?.result?.service_fee ?? 0
    ) || 0;
  }

  get fareGroups(): Array<{ label: string; count: number; total: number }> {
    return this.tripFareGroups(this.bookingdetails);
  }

  private tripFareGroups(details: any): Array<{ label: string; count: number; total: number }> {
    const groups = new Map<string, { label: string; count: number; total: number }>();
    (details?.seatsSelected || []).forEach((seat: any) => {
      const type = String(seat?.space_type || 'standard').toLowerCase();
      const label = type === 'normal'
        ? 'Standard'
        : type.charAt(0).toUpperCase() + type.slice(1);
      const current = groups.get(label) || { label, count: 0, total: 0 };
      current.count += 1;
      current.total += Number(this.payableCurrency === 'UGX' ? seat?.fareUGX : seat?.fareKSH) || 0;
      groups.set(label, current);
    });
    const rows = Array.from(groups.values());
    if (rows.length === 1 && !rows[0].total) {
      const total = this.payableCurrency === 'UGX'
        ? Number(details?.totalfareUGX || 0)
        : Number(details?.totalfareKSH || 0);
      rows[0].total = Math.max(0, total - this.tripBookingFee(details));
    }
    return rows;
  }

  requestConfirm(): void {
    if (this.isChecking) {
      return;
    }
    this.isError = false;
    this.errorMessage = '';
    this.paymentState = 'waiting';
    this.isWaiting = true;
    this.confirmMpesa(true);
  }

  resendPrompt(): void {
    if (this.isResending || this.resendSeconds > 0) {
      return;
    }
    const bookingId = this.paymentId(this.confirmData?.confirm_data);
    const phoneNumber = String(this.confirmData?.confirm_data?.phoneno || '').replace(/\D/g, '');
    if (!bookingId || !phoneNumber) {
      this.isError = true;
      this.errorMessage = 'We could not resend the prompt because its booking reference or phone number is missing.';
      return;
    }

    this.isResending = true;
    this.isError = false;
    this.errorMessage = '';
    this.statusMessage = '';
    this.service.resendPrompt(bookingId, phoneNumber).subscribe({
      next: (response: any) => {
        this.isResending = false;
        if (response?.status === false) {
          this.isError = true;
          this.errorMessage = response?.message || 'We could not resend the payment prompt. Please try again.';
          return;
        }
        this.statusMessage = 'Payment prompt resent successfully.';
        this.pollStartedAt = Date.now();
        this.startResendCooldown();
        this.clearPoll();
        this.confirmMpesa();
      },
      error: () => {
        this.isResending = false;
        this.isError = true;
        this.errorMessage = 'We could not resend the payment prompt. Please try again.';
      }
    });
  }

  backToPayment(step: 'passenger' | 'payment' = 'payment'): void {
    sessionStorage.setItem('payment-flow-step', step);
    this.router.navigate(['/payments', 'back']);
  }

  contactSupport(): void {
    const number = this.config?.site?.supportNumber;
    const email = this.config?.site?.supportEmail;
    if (number) {
      window.location.href = `tel:${String(number).replace(/[^\d+]/g, '')}`;
    } else if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      this.router.navigate(['/contact-us']);
    }
  }

  private confirmMpesa(userInitiated = false): void {
    const paymentData = this.confirmData?.confirm_data;
    const payId = this.paymentId(paymentData);
    if (!payId) {
      this.isWaiting = false;
      this.isError = true;
      this.errorMessage = 'The payment reference is missing. Please return to payment and try again.';
      return;
    }

    this.isChecking = true;
    this.service.confirmV2(payId).subscribe({
      next: (response: any) => {
        this.isChecking = false;
        if (response?.status === 'confirmed') {
          this.completePayment(paymentData);
          return;
        }
        if (response?.status === 'cancelled') {
          const responseMessage = this.findCustomerMessage(response);
          this.failPayment(responseMessage || (response?.reason === 'validation'
            ? 'This payment request is no longer valid. Please return to payment and try again.'
            : 'The payment request was cancelled. You can retry from the payment step.'));
          return;
        } else if (userInitiated) {
          this.isError = true;
          this.errorMessage = 'Payment is still pending. Approve the request on your phone, then check again.';
        }
        if (!this.socketIsActive()) {
          this.schedulePoll();
        }
      },
      error: () => {
        this.isChecking = false;
        if (userInitiated) {
          this.isError = true;
          this.errorMessage = 'We could not check the payment yet. We will keep checking automatically.';
        }
        if (!this.socketIsActive()) {
          this.schedulePoll();
        }
      }
    });
  }

  private paymentId(data: any): string {
    return this.findPaymentReference(data);
  }

  private findPaymentReference(value: any): string {
    const keys = [
      'transaction_id', 'payment_reference', 'transaction_reference',
      'checkout_request_id', 'booking_id', 'reference'
    ];
    for (const key of keys) {
      const candidate = this.findNestedString(value, key);
      if (candidate) {
        return candidate;
      }
    }
    return '';
  }

  private connectPaymentSocket(): void {
    const reference = this.paymentId(this.confirmData?.confirm_data);
    if (!reference || typeof WebSocket === 'undefined') {
      return;
    }

    this.closePaymentSocket();
    const query = new URLSearchParams({
      transaction_id: reference,
      booking_id: reference,
      reference
    });
    const separator = environment.wsBaseUrl.includes('?') ? '&' : '?';
    this.paymentSocket = new WebSocket(`${environment.wsBaseUrl}${separator}${query.toString()}`);
    this.socketFallbackStarted = false;

    this.paymentSocket.onopen = () => {
      this.statusMessage = 'Waiting for payment confirmation…';
      this.paymentSocket?.send(JSON.stringify({
        action: 'subscribe',
        transaction_id: reference,
        booking_id: reference,
        reference
      }));
    };
    this.paymentSocket.onmessage = event => this.handleSocketMessage(event.data);
    this.paymentSocket.onerror = () => {
      this.statusMessage = 'Still checking your payment securely…';
    };
    this.paymentSocket.onclose = () => {
      this.paymentSocket = null;
      if (this.paymentState === 'waiting' && !this.socketFallbackStarted) {
        this.socketFallbackStarted = true;
        this.schedulePoll();
      }
    };
  }

  private handleSocketMessage(rawMessage: unknown): void {
    let payload: any = rawMessage;
    if (typeof rawMessage === 'string') {
      try {
        payload = JSON.parse(rawMessage);
      } catch {
        payload = { status: rawMessage };
      }
    }

    const status = this.findPaymentStatus(payload).toLowerCase();
    if (this.isFailedPaymentStatus(status, payload)) {
      const socketMessage = this.findCustomerMessage(payload);
      this.failPayment(socketMessage || (
        status.includes('cancel')
          ? 'The payment request was cancelled. You can retry from the payment step.'
          : 'The payment did not go through. You can retry from the payment step.'
      ));
      return;
    }
    if (this.isSuccessfulPaymentStatus(status)) {
      this.completePayment(this.confirmData?.confirm_data);
      return;
    }
    if (status) {
      this.statusMessage = 'Payment is being processed…';
    }
  }

  private findPaymentStatus(value: any): string {
    for (const key of ['payment_status', 'transaction_status', 'status', 'state', 'event']) {
      const status = this.findNestedString(value, key);
      if (status) {
        return status;
      }
    }
    return '';
  }

  private isFailedPaymentStatus(status: string, payload: any): boolean {
    const customerMessage = this.findCustomerMessage(payload);
    const normalized = `${status} ${customerMessage}`.replace(/[^a-z]+/g, ' ').trim();
    const hasFailureLanguage =
      /(^|\s)(cancelled|canceled|failed|failure|declined|expired|rejected|unpaid|unsuccessful|timeout|error)(\s|$)/.test(normalized) ||
      /(^|\s)not (paid|completed|successful)(\s|$)/.test(normalized);
    return hasFailureLanguage ||
      this.findNestedBoolean(payload, 'success') === false ||
      (this.findNestedBoolean(payload, 'paid') === false && hasFailureLanguage);
  }

  private isSuccessfulPaymentStatus(status: string): boolean {
    const normalized = status.replace(/[^a-z]+/g, ' ').trim();
    if (/(^|\s)(unpaid|unsuccessful|failed|not paid|not completed|not successful)(\s|$)/.test(normalized)) {
      return false;
    }
    return /(^|\s)(confirmed|successful|success|completed|complete|paid)(\s|$)/.test(normalized);
  }

  private findCustomerMessage(value: any): string {
    for (const key of [
      'customer_message', 'display_message', 'error_message',
      'pay_later_message', 'paylater_message', 'pay_later', 'paylater',
      'message', 'detail', 'description'
    ]) {
      const message = this.findNestedString(value, key);
      if (message) {
        return message;
      }
    }
    return '';
  }

  private findNestedBoolean(value: any, key: string, depth = 0): boolean | null {
    if (!value || typeof value !== 'object' || depth > 6) {
      return null;
    }
    if (typeof value[key] === 'boolean') {
      return value[key];
    }
    for (const child of Object.values(value)) {
      const found = this.findNestedBoolean(child, key, depth + 1);
      if (found !== null) {
        return found;
      }
    }
    return null;
  }

  private findNestedString(value: any, key: string, depth = 0): string {
    if (!value || typeof value !== 'object' || depth > 6) {
      return '';
    }
    const direct = value[key];
    if (direct !== undefined && direct !== null && String(direct).trim()) {
      return String(direct).trim();
    }
    for (const child of Object.values(value)) {
      const found = this.findNestedString(child, key, depth + 1);
      if (found) {
        return found;
      }
    }
    return '';
  }

  private socketIsActive(): boolean {
    return !!this.paymentSocket &&
      (this.paymentSocket.readyState === WebSocket.CONNECTING ||
        this.paymentSocket.readyState === WebSocket.OPEN);
  }

  private closePaymentSocket(): void {
    if (!this.paymentSocket) {
      return;
    }
    this.paymentSocket.onclose = null;
    this.paymentSocket.close();
    this.paymentSocket = null;
  }

  private failPayment(message: string): void {
    this.clearPoll();
    this.closePaymentSocket();
    this.paymentState = 'failed';
    this.isWaiting = false;
    this.isError = true;
    this.errorMessage = message;
  }

  private schedulePoll(): void {
    this.clearPoll();
    if (Date.now() - this.pollStartedAt >= this.pollTimeoutMs) {
      this.isWaiting = false;
      this.paymentState = 'failed';
      this.isError = true;
      this.errorMessage = 'The payment is taking longer than expected. Check your phone, then check the status again.';
      return;
    }
    this.pollTimer = setTimeout(() => this.confirmMpesa(), this.pollIntervalMs);
  }

  private clearPoll(): void {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private startResendCooldown(): void {
    this.clearResendTimer();
    this.resendSeconds = 30;
    this.resendTimer = setInterval(() => {
      this.resendSeconds = Math.max(0, this.resendSeconds - 1);
      if (this.resendSeconds === 0) {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer(): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  }

  private completePayment(paymentData: any): void {
    this.clearPoll();
    this.closePaymentSocket();
    this.isWaiting = false;
    const reserveData = {
      bookingdetails: this.bookingdetails,
      returndetails: this.returndetails || null,
      returning: !!this.returndetails,
      condfirmData: this.confirmData,
      successData: paymentData
    };
    const encoded = Base64.encode(JSON.stringify(reserveData));
    this.dataService.changeSuccessMessage(encoded);
    this.dataService.clearPaymentFormState();
    localStorage.setItem('success-buupass', encoded);
    sessionStorage.removeItem('payment-flow-step');
    sessionStorage.removeItem(LEGACY_PAYMENT_FORM_STORAGE_KEY);
    sessionStorage.removeItem(PAYMENT_FLOW_STORAGE_KEY);
    this.router.navigate(['/booking-successful', 'thank-you']);
  }
}
